'use client'

import { ChevronRight } from 'lucide-react'
import React, { useState } from 'react'
import { loginTeilnehmer } from '@/actions/teilnehmerAuth'
import { useAuth } from '@/hooks/useAuth'
import Link from 'next/link'

interface LoginFormProps {
  onSuccess?: (userData: any) => void // Optional callback for successful login
  redirectTo?: string // Optional redirect URL after login
  className?: string // Optional custom styling
}

export const LoginForm: React.FC<LoginFormProps> = ({
  onSuccess,
  redirectTo = '/dashboard',
  className = '',
}) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    saveLogin: false,
  })

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string>('')
  const { refreshAuth } = useAuth()

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    const loginFormData = new FormData()
    loginFormData.append('email', formData.email)
    loginFormData.append('password', formData.password)

    try {
      const result = await loginTeilnehmer(loginFormData)

      if (result.success && result.userData) {
        // Refresh auth state
        await refreshAuth()

        // Call success callback if provided
        if (onSuccess) {
          onSuccess(result.userData)
        } else {
          // Default behavior: redirect to dashboard or specified page
          window.location.href = redirectTo
        }
      } else {
        setError(result.error || 'Login fehlgeschlagen')
      }
    } catch (error) {
      setError('Ein Fehler ist aufgetreten. Bitte versuche es erneut.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className={`space-y-4 ${className}`}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex flex-col gap-0 rounded-xl border-2 border-gray-700 bg-white p-4">
          <label className="mb-2 block text-sm font-medium text-gray-700">E-Mail</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            className="w-full focus:border-transparent focus:ring-2 focus:ring-accent"
            placeholder="E-Mail"
            required
          />
        </div>

        <div className="flex flex-col gap-0 rounded-xl border-2 border-gray-700 bg-white p-4">
          <label className="mb-2 block text-sm font-medium text-gray-700">Passwort</label>
          <input
            type="password"
            name="password"
            value={formData.password}
            onChange={handleInputChange}
            className="w-full focus:border-transparent focus:ring-2 focus:ring-accent"
            placeholder="Passwort"
            required
          />
        </div>

        <div className="flex items-center justify-end">
          <Link href="/forgot-password" className="text-sm text-black hover:underline">
            Passwort vergessen?
          </Link>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="flex w-full items-center justify-center rounded-lg bg-accent py-4 font-semibold text-black disabled:opacity-50"
        >
          {isLoading ? (
            'Anmelden...'
          ) : (
            <>
              Anmelden
              <ChevronRight className="ml-2 h-5 w-5" />
            </>
          )}
        </button>

        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
      </form>
    </div>
  )
}
